import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';
const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch (_error) {
    return API_BASE_URL.replace(/\/+$/, '');
  }
})();
const API_PORT = (() => {
  try {
    return new URL(API_BASE_URL).port || '';
  } catch (_error) {
    return '';
  }
})();

const resolveMediaUrl = (url) => {
  if (!url) {
    return '';
  }

  const raw = String(url).trim();
  if (!raw) {
    return '';
  }

  if (/^(data:|blob:)/i.test(raw)) {
    return raw;
  }

  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      const isLoopback = ['localhost', '127.0.0.1', '::1'].includes(parsed.hostname);

      if (isLoopback && parsed.port && parsed.port !== API_PORT) {
        return `${API_ORIGIN}${parsed.pathname}${parsed.search}`;
      }

      return raw;
    }
  } catch (_error) {
    return '';
  }

  const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;
  return `${API_ORIGIN}${normalizedPath}`;
};

const ShopHomePage = () => {
  const { shopCode } = useParams();
  const history = useHistory();

  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingSpecialInstructions, setPendingSpecialInstructions] = useState('');

  useEffect(() => {
    loadShopData();
  }, [shopCode]);

  const loadShopData = async () => {
    try {
      setLoading(true);

      // Load shop details
      const shopResponse = await axios.get(`${API_BASE_URL}/api/vendor/shops/code/${shopCode}`);
      if (shopResponse.data.success) {
        const shopData = shopResponse.data.data;
        setShop(shopData);
        document.title = `${shopData.shopName} - Order Online`;

        // Load products
        const productsResponse = await axios.get(`${API_BASE_URL}/api/vendor/products/shop/${shopData.shopId}`);
        if (productsResponse.data.success) {
          setProducts(productsResponse.data.data);
        }

        // Load categories
        const categoriesResponse = await axios.get(`${API_BASE_URL}/api/vendor/products/categories/shop/${shopData.shopId}`);
        if (categoriesResponse.data.success) {
          setCategories(categoriesResponse.data.data);
        }
      } else {
        toast.error('Shop not found');
      }
    } catch (error) {
      console.error('Error loading shop:', error);
      toast.error('Failed to load shop details');
    } finally {
      setLoading(false);
    }
  };

  const openAddItemModal = (product) => {
    setPendingProduct(product);
    setPendingSpecialInstructions('');
    setShowAddItemModal(true);
  };

  const addToCart = (product, specialInstructions = '') => {
    const cartItem = {
      ...product,
      quantity: 1,
      specialInstructions: specialInstructions.trim(),
      cartItemId: `${product.productId}_${Date.now()}`
    };

    setCart([...cart, cartItem]);
    toast.success(`${product.productName} added to cart`);
  };

  const updateQuantity = (cartItemId, change) => {
    setCart(cart.map(item => {
      if (item.cartItemId === cartItemId) {
        const newQuantity = item.quantity + change;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (cartItemId) => {
    setCart(cart.filter(item => item.cartItemId !== cartItemId));
    toast.info('Item removed from cart');
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.discountPrice || item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const proceedToCheckout = () => {
    if (cart.length === 0) {
      toast.warning('Your cart is empty');
      return;
    }
    history.push(`/${shopCode}/checkout`, { cart, shop });
  };

  const quickInstructionChips = ['No tomatoes', 'No chilli', 'No onion or garlic'];
  const selectedInstructionSet = new Set(
    pendingSpecialInstructions
      .split('|')
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)
  );

  const toggleInstructionChip = (chip) => {
    const currentTokens = pendingSpecialInstructions
      .split('|')
      .map((token) => token.trim())
      .filter(Boolean);

    const alreadySelected = currentTokens.some((token) => token.toLowerCase() === chip.toLowerCase());
    const nextTokens = alreadySelected
      ? currentTokens.filter((token) => token.toLowerCase() !== chip.toLowerCase())
      : [...currentTokens, chip];

    setPendingSpecialInstructions(nextTokens.join(' | ').slice(0, 100));
  };

  const filteredProducts = selectedCategory
    ? products.filter(p => p.category?.categoryId === selectedCategory)
    : products;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">Shop not found</div>
      </div>
    );
  }

  if (shop.isTemporarilyClosed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{shop.shopName}</h1>
          <p className="text-red-600 text-lg mb-2">Temporarily Closed</p>
          {shop.closureReason && <p className="text-gray-600">{shop.closureReason}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-40">
        {resolveMediaUrl(shop.bannerUrl) && (
          <img src={resolveMediaUrl(shop.bannerUrl)} alt={shop.shopName} className="w-full h-48 object-cover" />
        )}
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {resolveMediaUrl(shop.logoUrl) && (
                <img src={resolveMediaUrl(shop.logoUrl)} alt={shop.shopName} className="w-16 h-16 rounded-full object-cover" />
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{shop.shopName}</h1>
                <p className="text-sm text-gray-600">{shop.description}</p>
                <p className="text-xs text-gray-500">{shop.city}, {shop.state}</p>
              </div>
            </div>
            <div className="relative">
              <button
                onClick={() => document.getElementById('cartDrawer').classList.toggle('hidden')}
                className="relative bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition flex items-center gap-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Cart ({getCartCount()})
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                    {getCartCount()}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="bg-white border-b sticky top-0 z-30">
          <div className="container mx-auto px-4 py-3">
            <div className="flex gap-2 overflow-x-auto">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full whitespace-nowrap ${!selectedCategory ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                All
              </button>
              {categories.map(category => (
                <button
                  key={category.categoryId}
                  onClick={() => setSelectedCategory(category.categoryId)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap ${selectedCategory === category.categoryId ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  {category.categoryName}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.filter(p => p.isAvailable).map(product => (
            <div key={product.productId} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition">
              {product.imageUrl && (
                <img src={resolveMediaUrl(product.imageUrl)} alt={product.productName} className="w-full h-48 object-cover" />
              )}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-bold text-gray-800">{product.productName}</h3>
                  {product.isVeg !== null && (
                    <span className={`text-xs px-2 py-1 rounded ${product.isVeg ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {product.isVeg ? '🌱' : '🍖'}
                    </span>
                  )}
                </div>
                {product.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                )}
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <span className="text-xl font-bold text-green-600">₹{product.discountPrice || product.price}</span>
                    {product.discountPrice && (
                      <span className="text-sm text-gray-400 line-through ml-2">₹{product.price}</span>
                    )}
                  </div>
                  {(product.isBestSeller || product.isRecommended) && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                      {product.isBestSeller ? '⭐ Bestseller' : '👍 Recommended'}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => openAddItemModal(product)}
                  className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition font-medium"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cart Drawer */}
      <div id="cartDrawer" className="fixed inset-y-0 right-0 w-full md:w-96 bg-white shadow-2xl z-50 transform transition-transform hidden">
        <div className="h-full flex flex-col">
          <div className="bg-blue-500 text-white p-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">Your Cart ({getCartCount()})</h2>
            <button
              onClick={() => document.getElementById('cartDrawer').classList.add('hidden')}
              className="text-white hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center text-gray-500 mt-10">
                <p>Your cart is empty</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.map(item => (
                  <div key={item.cartItemId} className="flex items-center gap-3 border-b pb-3">
                    <img src={resolveMediaUrl(item.imageUrl)} alt={item.productName} className="w-16 h-16 object-cover rounded" />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{item.productName}</h4>
                      <p className="text-sm text-gray-600">₹{item.discountPrice || item.price}</p>
                      {item.specialInstructions && (
                        <p className="text-xs text-gray-500 mt-1">Special request: {item.specialInstructions}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQuantity(item.cartItemId, -1)}
                          className="w-7 h-7 bg-gray-200 rounded hover:bg-gray-300"
                        >
                          -
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.cartItemId, 1)}
                          className="w-7 h-7 bg-gray-200 rounded hover:bg-gray-300"
                        >
                          +
                        </button>
                        <button
                          onClick={() => removeFromCart(item.cartItemId)}
                          className="ml-auto text-red-500 hover:text-red-700"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div className="border-t p-4">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-bold text-gray-800">Total:</span>
                <span className="text-2xl font-bold text-green-600">₹{getCartTotal().toFixed(2)}</span>
              </div>
              <button
                onClick={proceedToCheckout}
                className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition font-bold text-lg"
              >
                Proceed to Checkout
              </button>
            </div>
          )}
        </div>
      </div>

      {showAddItemModal && pendingProduct && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="border-b px-5 py-4">
              <h3 className="text-xl font-bold text-gray-900">Add item</h3>
              <p className="mt-1 text-sm text-gray-500">Optional cooking request for this item only</p>
            </div>
            <div className="space-y-4 p-5">
              <div className="rounded-2xl bg-gray-50 p-4">
                <div className="text-base font-semibold text-gray-900">{pendingProduct.productName}</div>
                <div className="mt-1 text-sm text-gray-600">₹{pendingProduct.discountPrice || pendingProduct.price}</div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-800">Cooking request (optional)</label>
                <textarea
                  value={pendingSpecialInstructions}
                  onChange={(e) => setPendingSpecialInstructions(e.target.value.slice(0, 100))}
                  rows="4"
                  maxLength="100"
                  placeholder="e.g. Don’t make it too spicy"
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
                <div className="mt-2 flex justify-end text-xs text-gray-400">{pendingSpecialInstructions.length}/100</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {quickInstructionChips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => toggleInstructionChip(chip)}
                    className={`rounded-full border px-4 py-2 text-sm transition ${selectedInstructionSet.has(chip.toLowerCase()) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50'}`}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 border-t px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddItemModal(false);
                  setPendingProduct(null);
                  setPendingSpecialInstructions('');
                }}
                className="flex-1 rounded-2xl border border-gray-300 py-3 font-semibold text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  addToCart(pendingProduct, pendingSpecialInstructions);
                  setShowAddItemModal(false);
                  setPendingProduct(null);
                  setPendingSpecialInstructions('');
                }}
                className="flex-[1.4] rounded-2xl bg-blue-600 py-3 font-semibold text-white shadow-lg shadow-blue-200"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopHomePage;
